import { useState, useEffect, useCallback } from 'react';
import useAuth from '@sdk/authentication/useAuth';
import { BreakCategory } from '@sdk/settings/types';
import {
  mapAPIAutomatedBreaksSettingsToUI,
  getAutomatedBreaksSettings,
} from '@sdk/automatedBreaks/mappers';

import { BreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.types';
import { useUserOrgSettings } from '@web/settings/UserOrgSettingsContext';
import { useSettingsErrorsContext } from '@web/settings/SettingsErrorContext';
import { TabType } from '@web/settings/types';
import {
  getAutomatedBreaksFieldNameErrors,
  getInitialBreaksSettingsFromInvalidFields,
} from '@web/settings/components/schedule/Breaks/breaks.utils';

export const useAutomatedBreaksSettings = () => {
  const { userOrgSettings, onUserOrgSettingsChange } = useUserOrgSettings();
  const automatedBreaks = getAutomatedBreaksSettings(userOrgSettings);
  const automatedBreaksUI = mapAPIAutomatedBreaksSettingsToUI(automatedBreaks);

  const updateAutomatedBreaksSettings = (value: { [key in string]: unknown }) => {
    onUserOrgSettingsChange(value);
  };

  return {
    automatedBreaks: automatedBreaksUI,
    updateAutomatedBreaksSettings,
    isAttestationsFeatureEnabled: userOrgSettings.orgFeatures.breaksAttestation,
  };
};

export const useBreaks = () => {
  const { currentUser } = useAuth();
  const { userOrgSettings, onUserOrgSettingsChange, initialUserOrgSettings } = useUserOrgSettings();
  const { shiftBreakType } = userOrgSettings;
  const [areBreaksEnabled, setAreBreaksEnabled] = useState(shiftBreakType !== BreakCategory.off);
  const { settingsErrors, removeErrors } = useSettingsErrorsContext();

  useEffect(() => {
    setAreBreaksEnabled(shiftBreakType !== BreakCategory.off);
  }, [shiftBreakType]);

  const setBreaksAreEnabled = useCallback(
    (value: boolean): void => {
      let breaksSettingsForUpdate: BreaksSettings | null = null;

      if (!value) {
        const errors = getAutomatedBreaksFieldNameErrors(settingsErrors[TabType.schedule]);
        if (errors.length) {
          breaksSettingsForUpdate = getInitialBreaksSettingsFromInvalidFields(
            errors,
            initialUserOrgSettings
          );
        }

        removeErrors(TabType.schedule, errors);
      }

      const turnedOnValue =
        shiftBreakType === BreakCategory.paid ? BreakCategory.paid : BreakCategory.unpaid;
      setAreBreaksEnabled(value);

      if (breaksSettingsForUpdate) {
        breaksSettingsForUpdate = {
          ...breaksSettingsForUpdate,
          shiftBreakType: value ? turnedOnValue : BreakCategory.off,
        };
      } else {
        breaksSettingsForUpdate = { shiftBreakType: value ? turnedOnValue : BreakCategory.off };
      }

      onUserOrgSettingsChange(breaksSettingsForUpdate);
    },
    [shiftBreakType, onUserOrgSettingsChange, settingsErrors, removeErrors, initialUserOrgSettings]
  );

  return {
    currentUser,
    areBreaksEnabled,
    setBreaksAreEnabled,
  };
};
